import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { toast } from "sonner";
import { playNotificationSound, isDndActive, warmUpAudio, setRemoteSoundGrant, unlockAudio } from "@/lib/notificationSounds";
import { NotificationToast } from "@/components/notifications/NotificationToast";
import { getRecentLocalIds } from "@/lib/notifyAndToast";
import { getPusherClient, disconnectPusher } from "@/lib/pusherClient";
import { usePusherConfig } from "@/hooks/usePusherConfig";
import type { SoundTone } from "@/lib/notificationSounds";
import type { Channel } from "pusher-js";
import React from "react";

interface SoundPrefs {
  sound_enabled: boolean;
  sound_tone: SoundTone;
  dnd_enabled: boolean;
  dnd_start: string;
  dnd_end: string;
  toast_enabled: boolean;
}

function showCustomToast(n: any) {
  toast.custom(
    (id) =>
      React.createElement(NotificationToast, {
        id,
        title: n.title,
        message: n.message,
        severity: n.severity,
        link: n.link,
      }),
    { duration: 6000 }
  );
}

/**
 * Global notification listener — mount once in UserLayout / AdminLayout.
 * Uses Pusher for realtime delivery when enabled, falls back to Supabase Realtime.
 */
export function useNotificationListener() {
  const { user } = useAuth();
  const { isStaff, loading: permissionsLoading } = useAdminPermissions();
  const { key, cluster, enabled: pusherEnabled } = usePusherConfig();
  const soundPrefsRef = useRef<SoundPrefs>({
    sound_enabled: true,
    sound_tone: "ringtone",
    dnd_enabled: false,
    dnd_start: "22:00",
    dnd_end: "08:00",
    toast_enabled: true,
  });
  const browserPushRef = useRef(false);
  const userChannelRef = useRef<Channel | null>(null);
  const adminChannelRef = useRef<Channel | null>(null);

  // Warm up audio context on first user interaction
  useEffect(() => {
    warmUpAudio();
  }, []);

  // Sync sound/DND preferences
  useEffect(() => {
    if (!user) return;

    const applyPrefs = (d: any) => {
      soundPrefsRef.current = {
        sound_enabled: d.sound_enabled ?? true,
        sound_tone: d.sound_tone ?? "ringtone",
        dnd_enabled: d.dnd_enabled ?? false,
        dnd_start: d.dnd_start ?? "22:00",
        dnd_end: d.dnd_end ?? "08:00",
        toast_enabled: d.toast_enabled ?? true,
      };
      browserPushRef.current = d.browser_push_enabled ?? false;
      // The account remembers that the user already allowed sound — keep the
      // warm-up listeners armed and resume the context as soon as possible.
      if (d.sound_enabled !== false && d.sound_unlocked) {
        setRemoteSoundGrant(true);
        void unlockAudio();
      }
    };

    supabase
      .from("notification_preferences")
      .select("sound_enabled, sound_tone, dnd_enabled, dnd_start, dnd_end, browser_push_enabled, toast_enabled, sound_unlocked")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        if (data) applyPrefs(data);
      });
  }, [user]);

  const handleNotification = useCallback((newNotif: any) => {
    if (getRecentLocalIds().has(newNotif.id)) {
      return;
    }

    const sp = soundPrefsRef.current;
    const silenced = isDndActive(sp.dnd_enabled, sp.dnd_start, sp.dnd_end);

    if (sp.sound_enabled && !silenced) {
      playNotificationSound(sp.sound_tone);
    }

    if (sp.toast_enabled && !silenced) {
      showCustomToast(newNotif);
    }

    // Browser push when tab not focused
    if (
      browserPushRef.current &&
      typeof window.Notification !== "undefined" &&
      window.Notification.permission === "granted" &&
      !document.hasFocus()
    ) {
      try {
        const notif = new window.Notification(newNotif.title, {
          body: newNotif.message,
          icon: "/favicon.ico",
          tag: newNotif.id,
        });
        if (newNotif.link) {
          notif.onclick = () => {
            window.focus();
            window.location.assign(newNotif.link);
            notif.close();
          };
        }
        setTimeout(() => { try { notif.close(); } catch (_) {} }, 8000);
      } catch (_) {}
    }
  }, []);

  const handleAdminNotification = useCallback((newNotif: any) => {
    const sp = soundPrefsRef.current;
    const silenced = isDndActive(sp.dnd_enabled, sp.dnd_start, sp.dnd_end);

    if (sp.sound_enabled && !silenced) {
      playNotificationSound(sp.sound_tone);
    }

    if (sp.toast_enabled && !silenced) {
      showCustomToast({
        title: newNotif.title,
        message: newNotif.message,
        severity: "info",
        link: newNotif.link,
      });
    }
  }, []);

  // Main listener — Pusher or Supabase Realtime
  useEffect(() => {
    if (!user) return;

    // ── Pusher path ──
    if (pusherEnabled && key && cluster) {
      const pusher = getPusherClient(key, cluster);
      if (!pusher) return;

      const userChannel = pusher.subscribe(`private-user.${user.id}`);
      userChannel.bind("notification", (data: any) => {
        handleNotification(data);
      });
      userChannelRef.current = userChannel;

      return () => {
        userChannel.unbind_all();
        pusher.unsubscribe(`private-user.${user.id}`);
        userChannelRef.current = null;
      };
    }

    // ── Supabase Realtime fallback ──
    const channel = supabase
      .channel(`notifications-listener-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => handleNotification(payload.new as any)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, pusherEnabled, key, cluster, handleNotification]);

  // Admin channel — Pusher or Supabase Realtime
  useEffect(() => {
    if (!user || permissionsLoading || !isStaff) return;

    // ── Supabase Realtime fallback ──
    const adminChannel = supabase
      .channel(`admin-notif-listener-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "admin_notifications",
        },
        (payload) => handleAdminNotification(payload.new as any)
      )
      .subscribe();

    return () => {
      supabase.removeChannel(adminChannel);
    };
  }, [user, isStaff, permissionsLoading, handleAdminNotification]);

  // Cleanup on logout
  useEffect(() => {
    return () => {
      disconnectPusher();
    };
  }, []);
}
